import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api';

const api = axios.create({
  baseURL: `${API_URL}${API_PREFIX}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// User APIs
export const userAPI = {
  getUsers: (role) => api.get('/users', { params: { role } }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  regenerateApiKey: (id) => api.post(`/users/${id}/regenerate-key`),
  updateApiSettings: (id, data) => api.put(`/users/${id}/api-settings`, data),
};

// Wallet APIs
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  addBalance: (userId, amount) => api.post(`/wallet/add-balance?user_id=${userId}&amount=${amount}`),
  transfer: (data) => api.post('/wallet/transfer', data),
  creditDebit: (data) => api.post('/wallet/credit-debit', data),
  getTransactions: (limit) => api.get('/wallet/transactions', { params: { limit } }),
  getUserTransactions: (userId, limit) => api.get(`/wallet/transactions/${userId}`, { params: { limit } }),
};

// Recharge APIs
export const rechargeAPI = {
  perform: (data) => api.post('/recharge', data),
  getRecharges: (paramsOrLimit, status, startDate, endDate, userId, operator) => {
    let params;
    if (typeof paramsOrLimit === 'object') {
      params = paramsOrLimit;
    } else {
      params = { limit: paramsOrLimit };
      if (status) params.status = status;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (userId) params.user_id = userId;
      if (operator) params.operator = operator;
    }
    return api.get('/recharges', { params });
  },
  getRecharge: (id) => api.get(`/recharges/${id}`),
  getRechargeStatus: (id) => api.get(`/recharge/status/${id}`),
  getOperators: () => api.get('/operators'),
  getPlans: (operator, rechargeType) => api.get('/plans', { params: { operator, recharge_type: rechargeType } }),
  browsePlansLive: (operator, circle) => api.get('/plans/browse', { params: { operator, circle } }),
  getOffersLive: (number, operator) => api.get('/plans/offers', { params: { number, operator } }),
  browseDthPlans: (operator) => api.get('/plans/dth', { params: { operator } }),
  getDthInfo: (number, operator) => api.get('/plans/dth-info', { params: { number, operator } }),
  getDthHeavyPlans: (number, operator) => api.get('/plans/dth-heavy', { params: { number, operator } }),
  detectOperator: (number) => api.get('/detect-operator', { params: { number } }),
  getCommissionRates: (operator, service) => api.get('/recharge/commission-rates', { params: { operator, service } }),
  refund: (rechargeId) => api.post('/recharge/refund', { recharge_id: rechargeId }),
  checkStatus: (id) => api.post(`/recharges/${id}/check-status`),
  checkAllPending: () => api.post('/recharges/check-all-pending'),
  manualStatusChange: (id, data) => api.put(`/recharges/${id}/manual-status`, data),
  // Bill Payment
  getBillOperators: (serviceType) => api.get('/bill/operators', { params: { service_type: serviceType } }),
  fetchBill: (data) => api.post('/bill/fetch', data),
  payBill: (data) => api.post('/bill/pay', data),
};

// Commission APIs
export const commissionAPI = {
  getCommissions: (limit) => api.get('/commissions', { params: { limit } }),
  getSummary: () => api.get('/commissions/summary'),
};

// Settings APIs
export const settingsAPI = {
  getCommissionSettings: () => api.get('/settings/commission'),
  updateCommissionSettings: (data) => api.put('/settings/commission', data),
  getApiSettings: () => api.get('/settings/api'),
  updateApiSettings: (data) => api.put('/settings/api', data),
  testWhatsApp: (phone) => api.post(`/settings/test-whatsapp?phone=${phone}`),
  getSiteSettings: () => api.get('/settings/site'),
  updateSiteSettings: (data) => api.put('/settings/site', data),
  getAdminSettings: () => api.get('/settings/admin'),
  updateAdminSettings: (data) => api.put('/settings/admin', data),
  getServerInfo: () => api.get('/settings/server-info'),
  getStatsHistory: (minutes) => api.get('/settings/stats-history', { params: { minutes } }),
  getApiConfig: () => api.get('/settings/api-config'),
  updateApiConfig: (data) => api.put('/settings/api-config', data),
};

// Banks APIs
export const banksAPI = {
  list: () => api.get('/banks'),
  create: (data) => api.post('/banks', data),
  update: (id, data) => api.put(`/banks/${id}`, data),
  delete: (id) => api.delete(`/banks/${id}`),
};

// News APIs
export const newsAPI = {
  list: () => api.get('/news'),
  create: (data) => api.post('/news', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`),
};

// WhatsApp API Manager
export const whatsappApiAPI = {
  list: () => api.get('/whatsapp-apis'),
  create: (data) => api.post('/whatsapp-apis', data),
  update: (id, data) => api.put(`/whatsapp-apis/${id}`, data),
  delete: (id) => api.delete(`/whatsapp-apis/${id}`),
};

// WhatsApp Templates
export const whatsappTemplateAPI = {
  list: () => api.get('/whatsapp-templates'),
  create: (data) => api.post('/whatsapp-templates', data),
  update: (id, data) => api.put(`/whatsapp-templates/${id}`, data),
  delete: (id) => api.delete(`/whatsapp-templates/${id}`),
};

// Recharge API Manager
export const rechargeApiManagerAPI = {
  list: () => api.get('/recharge-apis'),
  create: (data) => api.post('/recharge-apis', data),
  update: (id, data) => api.put(`/recharge-apis/${id}`, data),
  delete: (id) => api.delete(`/recharge-apis/${id}`),
};

// Operator API Routes (Change API)
export const operatorApiRoutesAPI = {
  list: () => api.get('/operator-api-routes'),
  save: (data) => api.post('/operator-api-routes', data),
  delete: (id) => api.delete(`/operator-api-routes/${id}`),
};


// Plan API Circles
export const planApiCirclesAPI = {
  list: () => api.get('/plan-api/circles'),
  create: (data) => api.post('/plan-api/circles', data),
  update: (id, data) => api.put(`/plan-api/circles/${id}`, data),
  delete: (id) => api.delete(`/plan-api/circles/${id}`),
};

// Plan API Operators
export const planApiOperatorsAPI = {
  list: (apiType) => api.get(`/plan-api/operators${apiType ? '?api_type=' + apiType : ''}`),
  create: (data) => api.post('/plan-api/operators', data),
  update: (id, data) => api.put(`/plan-api/operators/${id}`, data),
  delete: (id) => api.delete(`/plan-api/operators/${id}`),
};

// Operator Codes
export const operatorCodesAPI = {
  list: () => api.get('/operator-codes'),
  create: (data) => api.post('/operator-codes', data),
  update: (id, data) => api.put(`/operator-codes/${id}`, data),
  delete: (id) => api.delete(`/operator-codes/${id}`),
};

// Commission Packages
export const commissionPackagesAPI = {
  list: (params) => api.get('/commission-packages', { params }),
  create: (data) => api.post('/commission-packages', data),
  update: (id, data) => api.put(`/commission-packages/${id}`, data),
  delete: (id) => api.delete(`/commission-packages/${id}`),
};

// Commission Chart (for retailers/distributors to see their rates)
export const commissionChartAPI = {
  getMy: () => api.get('/my-commission-chart'),
};

// Operator Commissions
export const operatorCommissionsAPI = {
  list: (params) => api.get('/operator-commissions', { params }),
  create: (data) => api.post('/operator-commissions', data),
  update: (id, data) => api.put(`/operator-commissions/${id}`, data),
  delete: (id) => api.delete(`/operator-commissions/${id}`),
};

// Commission Ranges
export const commissionRangesAPI = {
  list: (params) => api.get('/commission-ranges', { params }),
  create: (data) => api.post('/commission-ranges', data),
  update: (id, data) => api.put(`/commission-ranges/${id}`, data),
  delete: (id) => api.delete(`/commission-ranges/${id}`),
};

// Amount Ranges
export const amountRangesAPI = {
  list: () => api.get('/amount-ranges'),
  create: (data) => api.post('/amount-ranges', data),
  update: (id, data) => api.put(`/amount-ranges/${id}`, data),
  delete: (id) => api.delete(`/amount-ranges/${id}`),
};

// Invalid Amounts
export const invalidAmountsAPI = {
  list: () => api.get('/invalid-amounts'),
  create: (data) => api.post('/invalid-amounts', data),
  update: (id, data) => api.put(`/invalid-amounts/${id}`, data),
  delete: (id) => api.delete(`/invalid-amounts/${id}`),
};

// User Operator Blocks
export const userOperatorBlocksAPI = {
  list: () => api.get('/user-operator-blocks'),
  create: (data) => api.post('/user-operator-blocks', data),
  update: (id, data) => api.put(`/user-operator-blocks/${id}`, data),
  delete: (id) => api.delete(`/user-operator-blocks/${id}`),
};

// Bill Operators (Admin CRUD)
export const billOperatorsAPI = {
  list: () => api.get('/bill-operators'),
  create: (data) => api.post('/bill-operators', data),
  update: (id, data) => api.put(`/bill-operators/${id}`, data),
  delete: (id) => api.delete(`/bill-operators/${id}`),
};

// Banners
export const bannersAPI = {
  list: (pageType) => api.get('/banners', { params: { page_type: pageType } }),
  upload: (formData) => api.post('/banners/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/banners/${id}`, data),
  delete: (id) => api.delete(`/banners/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getChartData: (days) => api.get('/dashboard/chart-data', { params: { days } }),
  getRetailerStats: () => api.get('/dashboard/retailer-stats'),
  getApiBalance: () => api.get('/api-balance'),
  getAdminOverview: () => api.get('/dashboard/admin-overview'),
};

// Reports APIs
export const reportsAPI = {
  getTransactions: (startDate, endDate) => api.get('/reports/transactions', { params: { start_date: startDate, end_date: endDate } }),
  getCommissions: (startDate, endDate) => api.get('/reports/commissions', { params: { start_date: startDate, end_date: endDate } }),
  exportPdf: (reportType, startDate, endDate) => api.get('/reports/export-pdf', {
    params: { report_type: reportType, start_date: startDate, end_date: endDate },
    responseType: 'blob',
  }),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
